import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Main, MainTop, MainTopTitle, MainBody, WhiteBox } from '@/components/Layout'
import {
  BsrsGrid,
  DateCell,
  DateFilterCell,
  EnumFilterCell,
  EnumCell,
  actionButtonsColumnProps,
  commandColumnProps,
  BooleanColumn
} from '@/components/BsrsGrid'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import { Row, Col } from 'reactstrap'
import context from '@/context'
import { actionCreators } from '@/store/ReportTemplateFile'
import { actionCreators as categoryActionCreators } from '@/store/reportTemplateCategory'
import Resx from '@/components/Resx'
import ErrorBoundary from '@/components/ErrorBoundary'
import ReportTemplatesFileAttachments from './attachments'
import { downloadFile } from '@/utils/downloader'
import { dialogTypes } from '@/dialogs'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { reportType } from '@/enums/reportType'
import { Button } from '@/components/button'
import moment from 'moment'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { useDispatch } from '@/hooks/dispatch'
import { useLocalization } from '@/hooks/localization'
import { useDialog } from '@/hooks/useDialog'

const ReportTemplatesList = () => {
  const openDialog = useDialog()
  const reportTemplateFile = useSelector(state => state.reportTemplateFile)
  const reportTemplateCategory = useSelector(state => state.reportTemplateCategory)
  const isGranted = usePermissionChecker()
  const localize = useLocalization().localize
  const [activeTab, setActiveTab] = useState('templates')
  const [gridKey, setGridKey] = useState(moment())
  const isReadOnly = !isGranted('CanManageReportTemplates')
  const enumName = reportType.enumName
  const [ addAttachments,
    activate,
    deleteCategory ] = useDispatch([
    actionCreators.addAttachments,
    actionCreators.activate,
    categoryActionCreators.delete
  ])

  const openEditCategoryDialog = ({ id }) => openDialog({
    dialogType: dialogTypes.editReportTemplateCategory,
    id
  })
  const reloadGrid = () => setTimeout(() => setGridKey(moment()), 500)
  const enableItem = (it) => context.api.reportTemplateService.activateReportTemplate(it.id).then(() => reloadGrid()).catch()
  const disableItem = (it) => context.api.reportTemplateService.deactivateReportTemplate(it.id).then(() => reloadGrid()).catch()

  return (
    <Main>
      <MainTop>
        <MainTopTitle><Resx name='Reporting.ReportTemplates' /></MainTopTitle>
      </MainTop>
      <MainBody transparent>
        <WhiteBox>
          <TabNavItems activeTabId={activeTab} onClick={activeTab => setActiveTab(activeTab)} items={[
            { id: 'templates', text: 'Reporting.ReportTemplates', permissions: ['CanViewReportTemplates', 'CanManageReportTemplates'] },
            { id: 'imports', text: 'Reporting.Imports', permissions: ['CanManageReportTemplates'] },
            { id: 'categories', text: 'Common.Categories', permissions: ['CanViewReportTemplates', 'CanManageReportTemplates'] }
          ].filter(x => x.permissions.filter(p => isGranted(p)).length)} />
          <TabContent activeTab={activeTab} renderActiveOnly>
            <TabPane tabId='templates'>
              <WhiteBox header='Reporting.ReportTemplates' transparent>
                <BsrsGrid
                  persistenceId={'administration-report-templates-main'}
                  dataSource={context.api.reportTemplateService.getReportTemplateTableData}
                  filterable
                  lastReloadedAt={gridKey}>
                  <Column field='code' title={localize('Reporting.TemplateCode')} editable={false} />
                  <Column field='order' title={localize('Common.Order')} filter='numeric' editable={false} />
                  <Column field='reportTemplateVersionName' title={localize('Common.Name')} editable={false} />
                  <Column field='reportTemplateVersionLatinName' title={localize('Common.LatinName')} editable={false} />
                  <Column field='reportTemplateCategoryName' title={localize('Reporting.ReportTemplateCategoryName')} editable={false} />
                  <Column field='reportTemplateCategoryLatinName' title={localize('Reporting.ReportTemplateCategoryLatinName')} editable={false} />
                  {BooleanColumn({ field: 'isDisabled', title: localize('Reporting.IsDisabled') })}
                  <Column field='reportType' title={localize('Reporting.ReportType')} editable={false}
                    cell={EnumCell({ localize, enumName })}
                    filterCell={EnumFilterCell({ localize, enumName }, reportType.values)} enumName={enumName} />

                  <Column {...commandColumnProps({
                    detailUrl: x => `/administration-report-templates/${x.id}`,
                    canToggleActivation: !isReadOnly,
                    itemIsActive: x => !x.isDisabled,
                    onDisableClick: x => disableItem(x),
                    onEnableClick: x => enableItem(x),
                    additionalButtons: dataItem => [{
                      icon: 'pencil',
                      title: localize('Common.Edit'),
                      readonly: { isReadOnly },
                      onClick: () => openDialog({
                        dialogType: dialogTypes.selectReportTemplateCategory,
                        title: 'Common.Edit',
                        id: dataItem.id
                      })
                    }]
                  })} />
                </BsrsGrid>
              </WhiteBox>
            </TabPane>
            <TabPane tabId='imports'>
              <WhiteBox header='Reporting.UploadTemplatesDefinition' transparent>
                <Row>
                  <Col sm='12'>
                    <ErrorBoundary>
                      <ReportTemplatesFileAttachments
                        key={reportTemplateFile.attachmentsKey}
                        parentId={0} // TODO: currently not related to report templates
                        addAttachments={addAttachments}
                        adding={reportTemplateFile.adding}
                      />
                    </ErrorBoundary>
                  </Col>
                </Row>
              </WhiteBox>
              <WhiteBox header='Reporting.ReportTemplates' transparent>
                <BsrsGrid
                  persistenceId={'administration-report-templates-imports'}
                  dataSource={context.api.reportTemplateFileService.getFileTableData}
                  filterable
                  lastReloadedAt={reportTemplateFile.gridKey}>
                  <Column field='id' title={localize('Common.Id')} filter='numeric' editable={false} />
                  <Column field='fileAttachmentName' title={localize('Reporting.Filename')} editable={false} />
                  <Column field='reportName' title={localize('Reporting.ReportName')} editable={false} />
                  <Column field='version' title={localize('Reporting.Version')} filter='numeric' editable={false} />
                  <Column field='validFrom' title={localize('Common.ValidFrom')} filter='date' cell={DateCell} filterCell={DateFilterCell} editable={false} />
                  <Column field='validTo' title={localize('Common.ValidTo')} filter='date' cell={DateCell} filterCell={DateFilterCell} editable={false} />
                  {BooleanColumn({ field: 'isActive', title: localize('Common.IsActive') })}
                  <Column {...actionButtonsColumnProps(dataItem => [{
                    icon: dataItem.isActive ? 'power-off power-off--off' : 'power-off power-off--on',
                    title: localize(dataItem.isActive ? 'Common.Deactivate' : 'Common.Activate'),
                    onClick: () => activate({ entityId: dataItem.id, isActive: !dataItem.isActive })
                  }, {
                    icon: 'download',
                    title: localize(dataItem.fileAttachmentVerified !== true
                      ? (dataItem.fileAttachmentVerified === null ? 'Common.NotVerifiedYet' : 'Common.VerificationFailed')
                      : 'Common.Download'),
                    disabled: !dataItem.isActive || dataItem.fileAttachmentVerified !== true,
                    onClick: () => downloadFile(dataItem.fileAttachmentId, context.api.reportTemplateFileService.download)
                  }, {
                    icon: 'bxs-right-arrow-square green',
                    title: localize('Reporting.ImportTemplate'),
                    disabled: !!(!dataItem.isActive || dataItem.fileAttachmentVerified !== true || dataItem.version),
                    onClick: () => openDialog({
                      dialogType: dialogTypes.reportTemplateImport,
                      fileAttachmentId: dataItem.fileAttachmentId
                    })
                  }
                  ])} />
                </BsrsGrid>
              </WhiteBox>
            </TabPane>
            <TabPane tabId='categories'>
              <WhiteBox header='Common.Categories' transparent>
                <Row>
                  <Col>
                    <Button color='primary' icon='plus' onClick={() => openEditCategoryDialog({ id: 0 })}>{localize('Reporting.AddNewCategory')}</Button>
                  </Col>
                </Row>
                <Row className='mt-4'>
                  <Col>
                    <BsrsGrid
                      persistenceId={'administration-report-templates-categories'}
                      dataSource={context.api.reportTemplateCategoryService.getCategoriesTableData}
                      filterable
                      lastReloadedAt={reportTemplateCategory.gridKey}>
                      <Column field='name' title={localize('Common.Name')} editable={false} />
                      <Column field='latinName' title={localize('Common.LatinName')} editable={false} />
                      <Column field='reportTemplateCount' title={localize('Reporting.Templates')} filter='numeric' editable={false} />
                      <Column {...commandColumnProps({
                        editAction: openEditCategoryDialog,
                        deleteDisabled: (dataItem) => {
                          return dataItem.reportTemplateCount > 0
                        },
                        deleteAction: ({ id }) => deleteCategory(id)
                      })} />
                    </BsrsGrid>
                  </Col>
                </Row>
              </WhiteBox>
            </TabPane>
          </TabContent>
        </WhiteBox>
      </MainBody>
    </Main>
  )
}

export default ReportTemplatesList



// WEBPACK FOOTER //
// ./src/internal-app/pages/administration/report-templates/list.js